import { useState, type FormEvent } from "react";
import { useListMedia, useCreateMedia, useDeleteMedia, getListMediaQueryKey, useListCompanies } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "wouter";
import { Plus, Video, Film, Newspaper, Mic, MessageSquare, ExternalLink, Trash2 } from "lucide-react";
import InfoTip from "@/components/info-tip";
import { useToast } from "@/hooks/use-toast";

const typeIcons: Record<string, typeof Video> = {
  video: Film,
  clip: Video,
  article: Newspaper,
  podcast: Mic,
  social_post: MessageSquare,
};

export default function Media({ cohortSlug }: { cohortSlug?: string } = {}) {
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: media, isLoading } = useListMedia({
    type: typeFilter !== "all" ? typeFilter as "video" | "clip" | "article" | "podcast" | "social_post" : undefined,
  });
  const { data: companies } = useListCompanies();
  const createMutation = useCreateMedia();
  const deleteMutation = useDeleteMedia();

  const companyMap = new Map((companies ?? []).map(c => [c.id, c.name]));

  const handleCreate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    createMutation.mutate(
      {
        data: {
          companyId: parseInt(form.get("companyId") as string, 10),
          title: form.get("title") as string,
          type: form.get("type") as "video" | "clip" | "article" | "podcast" | "social_post",
          url: form.get("url") as string,
          description: (form.get("description") as string) || null,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListMediaQueryKey() });
          setDialogOpen(false);
          toast({ title: "Media asset added" });
        },
      }
    );
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListMediaQueryKey() });
          toast({ title: "Media asset removed" });
        },
      }
    );
  };

  return (
    <div className="space-y-6" data-testid="page-media">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-serif font-bold tracking-tight">Media Library</h1>
            <InfoTip content="All media assets across the portfolio — press coverage, podcast appearances, demo videos, social media highlights, and more. Media tagged as 'approved for investors' shows up in investor microsites, acting as social proof for your companies." />
          </div>
          <p className="text-sm text-muted-foreground mt-1">Clips, videos, articles, and media proof across portfolio</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-add-media"><Plus className="w-4 h-4 mr-2" />Add Media</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif">Add Media Asset</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Company</Label>
                  <select name="companyId" className="w-full h-9 rounded-md border bg-background px-3 text-sm" required data-testid="select-media-company">
                    <option value="">Select company</option>
                    {companies?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <select name="type" className="w-full h-9 rounded-md border bg-background px-3 text-sm" required data-testid="select-media-type">
                    <option value="video">Video</option>
                    <option value="clip">Clip</option>
                    <option value="article">Article</option>
                    <option value="podcast">Podcast</option>
                    <option value="social_post">Social Post</option>
                  </select>
                </div>
              </div>
              <div className="space-y-2"><Label>Title</Label><Input name="title" required data-testid="input-media-title" /></div>
              <div className="space-y-2"><Label>URL</Label><Input name="url" type="url" required data-testid="input-media-url" /></div>
              <div className="space-y-2"><Label>Description</Label><Textarea name="description" data-testid="input-media-description" /></div>
              <Button type="submit" className="w-full" disabled={createMutation.isPending} data-testid="button-submit-media">
                {createMutation.isPending ? "Adding..." : "Add Media Asset"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Select value={typeFilter} onValueChange={setTypeFilter}>
        <SelectTrigger className="w-48" data-testid="select-filter-media-type">
          <SelectValue placeholder="All types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All types</SelectItem>
          <SelectItem value="video">Video</SelectItem>
          <SelectItem value="clip">Clip</SelectItem>
          <SelectItem value="article">Article</SelectItem>
          <SelectItem value="podcast">Podcast</SelectItem>
          <SelectItem value="social_post">Social Post</SelectItem>
        </SelectContent>
      </Select>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      ) : media && media.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {media.map((asset) => {
            const TypeIcon = typeIcons[asset.type] || Video;
            return (
              <Card key={asset.id} data-testid={`card-media-${asset.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="outline" className="text-xs gap-1 capitalize"><TypeIcon className="w-3 h-3" />{asset.type.replace("_", " ")}</Badge>
                    <div className="flex items-center gap-1">
                      <a href={asset.url} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-muted rounded" data-testid={`link-media-${asset.id}`}><ExternalLink className="w-3 h-3" /></a>
                      <button onClick={() => handleDelete(asset.id)} className="p-1 hover:bg-muted rounded" data-testid={`button-delete-media-${asset.id}`}><Trash2 className="w-3 h-3 text-muted-foreground" /></button>
                    </div>
                  </div>
                  <h4 className="font-medium text-sm">{asset.title}</h4>
                  {asset.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{asset.description}</p>}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t">
                    <Link href={cohortSlug ? `/c/${cohortSlug}/companies/${asset.companyId}` : `/companies/${asset.companyId}`}>
                      <span className="text-xs text-primary hover:underline cursor-pointer">{companyMap.get(asset.companyId) ?? `#${asset.companyId}`}</span>
                    </Link>
                    {asset.publishedAt && <span className="text-xs text-muted-foreground">{new Date(asset.publishedAt).toLocaleDateString()}</span>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Video className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium">No media assets found</p>
            <p className="text-sm text-muted-foreground mt-1">{typeFilter !== "all" ? "Try changing the filter" : "Link your first media asset"}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
